import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StreamingUgcTrackRow } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { formatStreamingNumber } from '../../streamingFormat';
import { shortVideoMessages as messages } from '../../shortVideoMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
import { UGC_PLATFORM_LOOKS } from './ugcPlatforms';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface UgcTrackTableProps {
  tracks: readonly StreamingUgcTrackRow[];
  sourceName: string;
}

/** "By track" (design streaming-sections c1): videos, views, likes and the average watch time. */
const UgcTrackTableComponent: React.FC<UgcTrackTableProps> = ({ tracks, sourceName }) => {
  const intl = useIntl();
  const rows = useMemo(
    () =>
      [...tracks]
        .sort((left, right) => (right.creations ?? 0) - (left.creations ?? 0))
        .map((track) => ({
          ...track,
          split: track.byPlatform
            .map((part) => `${UGC_PLATFORM_LOOKS[part.platform].shortName} ${intl.formatNumber(part.creations)}`)
            .join(' · '),
        })),
    [intl, tracks]
  );
  return (
    <StreamingBlock title={intl.formatMessage(messages.byTrack)} testId="streaming-shortVideo-tracks">
      <div className={shared.tableWrap}>
        <table className={shared.table}>
          <thead>
            <tr>
              <th>{intl.formatMessage(messages.trackColumn)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.videosColumn)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.views)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.likes)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.watchColumn)}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.externalId}>
                <td>
                  <b>{row.name}</b>
                  {row.split !== '' ? <div className={shared.faint}>{row.split}</div> : null}
                </td>
                <td className={shared.numCell}>{formatStreamingNumber(intl, row.creations)}</td>
                <td className={shared.numCell}>{formatStreamingNumber(intl, row.views)}</td>
                <td className={shared.numCell}>{formatStreamingNumber(intl, row.likes)}</td>
                <td className={shared.numCell}>
                  {row.avgWatchMs === null
                    ? '—'
                    : intl.formatMessage(messages.watchSeconds, {
                        seconds: intl.formatNumber(row.avgWatchMs / 1000, { maximumFractionDigits: 1 }),
                      })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={shared.footnote}>
        {sourceName} · {intl.formatMessage(messages.trackNote)}
      </p>
    </StreamingBlock>
  );
};

export const UgcTrackTable = TypedMemo(UgcTrackTableComponent);
